import { useQuery } from "convex/react";

import { Badge } from "@/components/ui/badge";
import {
	Card,
	CardContent,
	CardDescription,
	CardHeader,
	CardTitle,
} from "@/components/ui/card";
import { api } from "../../../../convex/_generated/api";
import type { Id } from "../../../../convex/_generated/dataModel";
import type { ClioMatterDetail } from "./types";

interface MatterOverviewProps {
	connectionId: Id<"oauthConnections">;
	matter: ClioMatterDetail;
}

function Field({
	label,
	value,
}: {
	label: string;
	value: string | number | boolean | null | undefined;
}) {
	const display =
		value === null || value === undefined || value === ""
			? "—"
			: typeof value === "boolean"
				? value
					? "Yes"
					: "No"
				: String(value);
	return (
		<div className="flex flex-col gap-1">
			<dt className="text-xs font-medium text-muted-foreground">{label}</dt>
			<dd className="text-sm">{display}</dd>
		</div>
	);
}

export function MatterOverview({ connectionId, matter }: MatterOverviewProps) {
	const contacts = useQuery(api.clio.queries.listMatterContacts, {
		connectionId,
		clioMatterId: matter.clioId,
	});
	const topContacts = (contacts ?? []).slice(0, 5);

	return (
		<div className="flex flex-col gap-4 p-4">
			<Card>
				<CardHeader>
					<CardTitle>Details</CardTitle>
					<CardDescription>Core matter metadata from Clio</CardDescription>
				</CardHeader>
				<CardContent>
					<dl className="grid grid-cols-1 gap-4 sm:grid-cols-2 md:grid-cols-3">
						<Field label="Matter number" value={matter.displayNumber} />
						<Field label="Status" value={matter.status} />
						<Field label="Matter stage" value={matter.matterStage} />
						<Field label="Client" value={matter.clientName} />
						<Field label="Client type" value={matter.clientType} />
						<Field
							label="Responsible attorney"
							value={matter.responsibleAttorneyName}
						/>
						<Field
							label="Originating attorney"
							value={matter.originatingAttorneyName}
						/>
						<Field label="Practice area" value={matter.practiceAreaName} />
						<Field label="Open date" value={matter.openDate} />
						<Field label="Pending date" value={matter.pendingDate} />
						<Field label="Close date" value={matter.closeDate} />
						<Field label="Billable" value={matter.billable} />
						<Field label="Currency" value={matter.currency} />
						<Field label="Permissions" value={matter.permissions} />
						<Field label="Maildrop" value={matter.maildrop} />
					</dl>
					{matter.description && (
						<div className="mt-4 space-y-1">
							<div className="text-xs font-medium text-muted-foreground">
								Description
							</div>
							<div className="text-sm whitespace-pre-wrap">
								{matter.description}
							</div>
						</div>
					)}
				</CardContent>
			</Card>
			<Card>
				<CardHeader>
					<CardTitle>Contacts</CardTitle>
					<CardDescription>Top parties linked to this matter</CardDescription>
				</CardHeader>
				<CardContent>
					{topContacts.length === 0 ? (
						<p className="text-sm text-muted-foreground">
							No contacts synced yet.
						</p>
					) : (
						<ul className="flex flex-col gap-2">
							{topContacts.map((entry) => (
								<li
									key={entry._id}
									className="flex items-start justify-between gap-3 rounded-md border p-3"
								>
									<div className="flex flex-col">
										<span className="font-medium text-sm">
											{entry.contact?.name ?? `Contact ${entry.contactClioId}`}
										</span>
										<span className="text-xs text-muted-foreground">
											{entry.contact?.primaryEmail ??
												entry.contact?.primaryPhone ??
												entry.contact?.type ??
												""}
										</span>
									</div>
									<div className="flex flex-col items-end gap-1">
										{entry.isClient && <Badge>Client</Badge>}
										{entry.relationship && (
											<Badge variant="outline">{entry.relationship}</Badge>
										)}
									</div>
								</li>
							))}
						</ul>
					)}
				</CardContent>
			</Card>
			{matter.customFieldValues.length > 0 && (
				<Card>
					<CardHeader>
						<CardTitle>Custom fields</CardTitle>
					</CardHeader>
					<CardContent>
						<dl className="grid grid-cols-1 gap-4 sm:grid-cols-2 md:grid-cols-3">
							{matter.customFieldValues.map((cf) => (
								<Field
									key={cf.clioId ?? cf.fieldName}
									label={cf.fieldName ?? "—"}
									value={cf.value ?? "—"}
								/>
							))}
						</dl>
					</CardContent>
				</Card>
			)}
		</div>
	);
}
